import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { AuthorizationService } from '../services/authorization.service';
import { LoggedUserService } from '../services/logged-user.service';
import { TenantConfigService } from '../services/tenant-config.service';
import { UserRole } from '../interfaces/user.interface';
import { type FeatureKey } from '../constants/feature-keys';

/**
 * Roles that have access to the Pagai HR application.
 * Only ADMIN, HR, and LABOR_CONSULTANT can use the app.
 */
export const APP_ALLOWED_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.HR,
  UserRole.LABOR_CONSULTANT,
];

/**
 * Guard that checks if user has access to the Pagai HR application.
 * Only ADMIN, HR, and LABOR_CONSULTANT roles are allowed.
 * Waits for user to be loaded before checking.
 * Redirects to /access-denied if user doesn't have access.
 */
export const hasAppAccessGuard: CanActivateFn = () => {
  const loggedUserService = inject(LoggedUserService);
  const router = inject(Router);

  return loggedUserService.user$.pipe(
    // Wait for user to be loaded (not null)
    filter((user) => user !== null),
    take(1),
    map((user) => {
      const userRoles = user?.roles ?? [];
      const hasAccess = userRoles.some((role) => APP_ALLOWED_ROLES.includes(role));

      if (hasAccess) {
        return true;
      }

      return router.createUrlTree(['/access-denied']);
    }),
  );
};

/**
 * CanMatch version of hasAppAccessGuard for route matching.
 */
export const hasAppAccessMatch: CanMatchFn = () => {
  const loggedUserService = inject(LoggedUserService);

  return loggedUserService.user$.pipe(
    filter((user) => user !== null),
    take(1),
    map((user) => {
      const userRoles = user?.roles ?? [];
      return userRoles.some((role) => APP_ALLOWED_ROLES.includes(role));
    }),
  );
};

/**
 * Guard factory that creates a guard for specific roles.
 * Redirects to home if user doesn't have any of the required roles.
 * Note: ADMIN is treated as equivalent to HR, not as a superuser.
 * @param allowedRoles - Array of roles that can access the route
 * @returns CanActivateFn guard function
 */
export function hasRoleGuard(allowedRoles: UserRole[]): CanActivateFn {
  return () => {
    const authz = inject(AuthorizationService);
    const router = inject(Router);

    if (authz.canAccess(allowedRoles)) {
      return true;
    }

    return router.createUrlTree(['/']);
  };
}

/**
 * Guard factory that gates a route behind a per-tenant feature flag.
 * Waits for the selected tenant to load, then allows the route only if the
 * feature is enabled (see {@link TenantConfigService}). Redirects home otherwise.
 * As a `CanMatchFn`, a disabled feature prevents the lazy chunk from loading.
 * @param feature - Feature flag key required to match the route
 */
export function featureGuard(feature: FeatureKey): CanMatchFn {
  return () => {
    const tenantConfig = inject(TenantConfigService);
    const router = inject(Router);

    return tenantConfig
      .hasFeature$(feature)
      .pipe(map((enabled) => (enabled ? true : router.createUrlTree(['/']))));
  };
}

/**
 * Guard that only allows Admin users.
 * Redirects to home if user is not an Admin.
 */
export const isAdminGuard: CanActivateFn = () => {
  const authz = inject(AuthorizationService);
  const router = inject(Router);

  if (authz.isAdmin()) {
    return true;
  }

  return router.createUrlTree(['/']);
};

/**
 * Guard that allows Admin or HR users.
 * ADMIN and HR are equivalent roles.
 */
export const isAdminOrHRGuard: CanActivateFn = () => {
  const authz = inject(AuthorizationService);
  const router = inject(Router);

  if (authz.isAdmin() || authz.isHR()) {
    return true;
  }

  return router.createUrlTree(['/']);
};

/**
 * Guard that allows Admin, HR, or Manager users.
 */
export const isAdminOrHROrManagerGuard: CanActivateFn = () => {
  const authz = inject(AuthorizationService);
  const router = inject(Router);

  if (authz.isAdmin() || authz.isHR() || authz.isManager()) {
    return true;
  }

  return router.createUrlTree(['/']);
};

/**
 * Guard that only allows Labor Consultant users.
 */
export const isLaborConsultantGuard: CanActivateFn = () => {
  const authz = inject(AuthorizationService);
  const router = inject(Router);

  if (authz.isLaborConsultant()) {
    return true;
  }

  return router.createUrlTree(['/']);
};

/**
 * Guard that blocks Labor Consultant users.
 * Useful for routes that only internal employees should access.
 */
export const notLaborConsultantGuard: CanActivateFn = () => {
  const authz = inject(AuthorizationService);
  const router = inject(Router);

  if (!authz.isLaborConsultant()) {
    return true;
  }

  return router.createUrlTree(['/']);
};
